import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import useMediaQuery from '@mui/material/useMediaQuery'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import styles from './styles.module.scss'
import '../../scss/App.scss'
import { StyledTooltip } from './Tooltip.config'

const Tooltip = ({
  symbol = '?',
  children = 'Example text',
  wrapperClassName,
  symbolClassName,
  wrapperStyle,
  symbolStyle,
}) => {
  const canHover = useMediaQuery('(hover: hover) and (pointer: fine)')
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    if (canHover) return
    if (!isOpen) return

    const closeOnScroll = () => {
      setIsOpen(false)
    }

    document.addEventListener('scroll', closeOnScroll, true)
    window.addEventListener('scroll', closeOnScroll, true)
    window.addEventListener('wheel', closeOnScroll, { passive: true })
    document.addEventListener('touchmove', closeOnScroll, { passive: true })
    document.addEventListener('pointermove', closeOnScroll, { passive: true })

    return () => {
      document.removeEventListener('scroll', closeOnScroll, true)
      window.removeEventListener('scroll', closeOnScroll, true)
      window.removeEventListener('wheel', closeOnScroll)
      document.removeEventListener('touchmove', closeOnScroll)
      document.removeEventListener('pointermove', closeOnScroll)
    }
  }, [canHover, isOpen])

  const popperProps = useMemo(
    () => ({
      modifiers: [
        {
          name: 'preventOverflow',
          options: {
            boundary: 'viewport',
            padding: 8,
          },
        },
        {
          name: 'flip',
          options: {
            padding: 8,
          },
        },
      ],
    }),
    []
  )

  const handleToggle = useCallback(() => {
    if (canHover) return
    setIsOpen((prev) => !prev)
  }, [canHover])

  const handleClose = useCallback(() => {
    if (canHover) return
    setIsOpen(false)
  }, [canHover])

  const tooltipProps = canHover
    ? {
        arrow: true,
        placement: 'right',
        PopperProps: popperProps,
      }
    : {
        arrow: true,
        placement: 'bottom',
        open: isOpen,
        disableHoverListener: true,
        disableFocusListener: true,
        disableTouchListener: true,
        PopperProps: popperProps,
        onClose: handleClose,
      }

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <div className={wrapperClassName || styles.wrapper} style={wrapperStyle}>
        <StyledTooltip title={children} {...tooltipProps}>
          <span className={symbolClassName || styles.symbol} style={symbolStyle} onClick={handleToggle}>
            {symbol}
          </span>
        </StyledTooltip>
      </div>
    </ClickAwayListener>
  )
}

export default Tooltip

export const SmartTooltip = ({ title, placement = 'right', children, ...rest }) => {
  const canHover = useMediaQuery('(hover: hover) and (pointer: fine)')
  const [isOpen, setIsOpen] = useState(false)

  const popperProps = useMemo(
    () => ({
      modifiers: [
        {
          name: 'preventOverflow',
          options: {
            boundary: 'viewport',
            padding: 8,
          },
        },
        {
          name: 'flip',
          options: {
            padding: 8,
          },
        },
      ],
    }),
    []
  )

  const handleToggle = useCallback(() => {
    if (canHover) return
    setIsOpen((prev) => !prev)
  }, [canHover])

  const handleClose = useCallback(() => {
    if (canHover) return
    setIsOpen(false)
  }, [canHover])

  useEffect(() => {
    if (canHover) return
    if (!isOpen) return

    const closeOnScroll = () => {
      setIsOpen(false)
    }

    // Note: scroll doesn't bubble, but it can be captured on document/window.
    // Using document capture helps with scrollable containers.
    document.addEventListener('scroll', closeOnScroll, true)
    window.addEventListener('scroll', closeOnScroll, true)
    window.addEventListener('wheel', closeOnScroll, { passive: true })
    document.addEventListener('touchmove', closeOnScroll, { passive: true })
    document.addEventListener('pointermove', closeOnScroll, { passive: true })

    return () => {
      document.removeEventListener('scroll', closeOnScroll, true)
      window.removeEventListener('scroll', closeOnScroll, true)
      window.removeEventListener('wheel', closeOnScroll)
      document.removeEventListener('touchmove', closeOnScroll)
      document.removeEventListener('pointermove', closeOnScroll)
    }
  }, [canHover, isOpen])

  const tooltipProps = canHover
    ? {
        arrow: true,
        placement,
        PopperProps: popperProps,
      }
    : {
        arrow: true,
        placement,
        open: isOpen,
        disableHoverListener: true,
        disableFocusListener: true,
        disableTouchListener: true,
        PopperProps: popperProps,
        onClose: handleClose,
      }

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <span onClick={handleToggle}>
        <StyledTooltip title={title} {...tooltipProps} {...rest}>
          <span>{children}</span>
        </StyledTooltip>
      </span>
    </ClickAwayListener>
  )
}

SmartTooltip.propTypes = {
  title: PropTypes.node.isRequired,
  placement: PropTypes.string,
  children: PropTypes.node.isRequired,
}

Tooltip.propTypes = {
  symbol: PropTypes.node,
  children: PropTypes.node.isRequired,
  wrapperClassName: PropTypes.string,
  symbolClassName: PropTypes.string,
  wrapperStyle: PropTypes.object,
  symbolStyle: PropTypes.object,
}
